import React, { useState, useEffect } from "react";
import axios from "axios";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, Calendar, DollarSign, Route } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLang } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { key: "day", label: "Hoje" },
  { key: "week", label: "7 dias" },
  { key: "month", label: "30 dias" },
  { key: "year", label: "12 meses" },
];

export default function EarningsDashboard() {
  const { t } = useLang();
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (p) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/driver/earnings?period=${p}`);
      setData(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(period); }, [period]);

  const stats = [
    { label: "Hoje", value: data?.today_total ?? 0, icon: Calendar, color: "text-blue-600 bg-blue-500/10" },
    { label: "Semana", value: data?.week_total ?? 0, icon: TrendingUp, color: "text-orange-500 bg-orange-500/10" },
    { label: "Mês", value: data?.month_total ?? 0, icon: DollarSign, color: "text-green-600 bg-green-500/10" },
    { label: "Total", value: data?.all_time_total ?? 0, icon: Route, color: "text-purple-600 bg-purple-500/10" },
  ];

  return (
    <Card className="p-5" data-testid="earnings-dashboard">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Ganhos</h2>
          <p className="text-xs text-muted-foreground">Acompanhe sua evolução</p>
        </div>
        <Tabs value={period} onValueChange={setPeriod}>
          <TabsList className="rounded-full">
            {PERIODS.map(p => (
              <TabsTrigger key={p.key} value={p.key} data-testid={`earn-period-${p.key}`} className="rounded-full text-xs">
                {p.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {stats.map(s => (
          <div key={s.label} className="p-3 rounded-xl border border-border bg-card/50" data-testid={`stat-${s.label.toLowerCase()}`}>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${s.color}`}>
              <s.icon className="w-4 h-4" />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{s.label}</div>
            <div className="text-lg font-bold tracking-tight">R$ {s.value.toFixed(2)}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="text-sm">
          <span className="text-muted-foreground">Total no período: </span>
          <span className="font-bold">R$ {(data?.period_total ?? 0).toFixed(2)}</span>
          <span className="text-muted-foreground ml-3">Corridas: </span>
          <span className="font-bold">{data?.period_rides ?? 0}</span>
        </div>
      </div>

      <div className="h-56 -mx-2" data-testid="earnings-chart">
        {loading && <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Carregando...</div>}
        {!loading && data && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="earnGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(221 83% 53%)" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="hsl(221 83% 53%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 32% 91%)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(215 16% 47%)" interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(215 16% 47%)" tickFormatter={(v) => `R$${v}`} width={50} />
              <Tooltip
                formatter={(v) => [`R$ ${Number(v).toFixed(2)}`, "Ganhos"]}
                contentStyle={{ background: "hsl(0 0% 100%)", border: "1px solid hsl(214 32% 91%)", borderRadius: 8, fontSize: 12 }}
              />
              <Area type="monotone" dataKey="value" stroke="hsl(221 83% 53%)" strokeWidth={2} fill="url(#earnGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Per-ride breakdown */}
      {data?.rides_breakdown && data.rides_breakdown.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border" data-testid="earnings-breakdown">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-sm font-semibold tracking-tight">Corridas creditadas <span className="text-muted-foreground font-normal">(últimas {data.rides_breakdown.length})</span></h3>
            <div className="flex gap-3 text-xs">
              <span className="text-muted-foreground">Bruto <b className="text-foreground">R$ {Number(data.rides_totals?.gross || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</b></span>
              <span className="text-muted-foreground">Comissão <b className="text-red-600">- R$ {Number(data.rides_totals?.platform_fee || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</b></span>
              <span className="text-muted-foreground">Líquido <b className="text-emerald-600">R$ {Number(data.rides_totals?.net_payout || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</b></span>
            </div>
          </div>
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 px-2 font-medium">Data</th>
                  <th className="py-2 px-2 font-medium">Rota</th>
                  <th className="py-2 px-2 font-medium text-right">Bruto</th>
                  <th className="py-2 px-2 font-medium text-right">Comissão</th>
                  <th className="py-2 px-2 font-medium text-right">Líquido</th>
                  <th className="py-2 px-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.rides_breakdown.map((r) => (
                  <tr key={r.ride_id} className="border-b border-border/50 hover:bg-muted/50" data-testid={`earnings-row-${r.ride_id}`}>
                    <td className="py-2 px-2 text-muted-foreground whitespace-nowrap">
                      {new Date(r.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}
                      <div className="text-[10px]">{new Date(r.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</div>
                    </td>
                    <td className="py-2 px-2 min-w-[180px] max-w-[280px]">
                      <div className="truncate" title={r.pickup_address}>{r.pickup_address || "—"}</div>
                      <div className="truncate text-muted-foreground" title={r.dest_address}>→ {r.dest_address || "—"}</div>
                    </td>
                    <td className="py-2 px-2 text-right font-medium whitespace-nowrap">
                      R$ {Number(r.gross || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-2 px-2 text-right text-red-600 whitespace-nowrap">
                      - R$ {Number(r.platform_fee || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-2 px-2 text-right font-bold text-emerald-600 whitespace-nowrap" data-testid={`earnings-net-${r.ride_id}`}>
                      R$ {Number(r.net_payout || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-2 justify-end">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-700"}`}>
                          {r.payment_status === "paid" ? "Pago" : "Pendente"}
                        </span>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const r2 = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/rides/${r.ride_id}/receipt.pdf`, { responseType: "blob" });
                              const blob = new Blob([r2.data], { type: "application/pdf" });
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = `recibo-${r.ride_id}.pdf`;
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              window.URL.revokeObjectURL(url);
                            } catch (err) {
                              console.error("PDF download failed", err);
                            }
                          }}
                          data-testid={`earnings-receipt-${r.ride_id}`}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30"
                          title="Baixar recibo PDF"
                        >
                          PDF
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}
